// =============================================
// EXPORTERS — CSV and PDF helpers
// =============================================
import jsPDF from 'jspdf';
import 'jspdf-autotable';

// ---------- CSV ----------
/**
 * Export an array of objects to a CSV file.
 * @param {Array<Object>} rows
 * @param {Array<{key: string, label: string}>} columns
 * @param {string} filename
 */
export function exportCSV(rows, columns, filename = 'export.csv') {
  if (!rows || rows.length === 0) {
    alert('No data to export');
    return;
  }

  // Build header
  const header = columns.map((c) => `"${c.label}"`).join(',');

  // Build body
  const body = rows
    .map((row) => {
      return columns
        .map((col) => {
          const val = row[col.key];
          if (val === null || val === undefined) return '""';
          // Escape quotes and wrap in quotes
          const str = String(val).replace(/"/g, '""');
          return `"${str}"`;
        })
        .join(',');
    })
    .join('\n');

  const csv = `${header}\n${body}`;

  // Trigger download
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// ---------- PDF ----------
/**
 * Export an array of objects to a PDF file.
 * @param {Object} opts
 * @param {string} opts.title      - Title of the report
 * @param {string} [opts.subtitle] - Subtitle (e.g., period)
 * @param {Array<Object>} opts.summaryRows - Optional key-value summary rows at top
 * @param {Array<Object>} opts.rows
 * @param {Array<{key: string, label: string, formatter?: Function}>} opts.columns
 * @param {string} [opts.filename]
 */
export function exportPDF({
  title = 'LRA Report',
  subtitle = '',
  summaryRows = [],
  rows = [],
  columns = [],
  filename = 'report.pdf',
}) {
  if (!rows || rows.length === 0) {
    alert('No data to export');
    return;
  }

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });

  // ----- Header -----
  doc.setFontSize(18);
  doc.setFont(undefined, 'bold');
  doc.text('LRA Inventory — Liberia Revenue Authority', 14, 18);

  doc.setFontSize(14);
  doc.setFont(undefined, 'normal');
  doc.text(title, 14, 28);

  if (subtitle) {
    doc.setFontSize(10);
    doc.setTextColor(120, 120, 120);
    doc.text(subtitle, 14, 34);
    doc.setTextColor(0, 0, 0);
  }

  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(
    `Generated: ${new Date().toLocaleString()}`,
    14,
    40
  );
  doc.setTextColor(0, 0, 0);

  let currentY = 46;

  // ----- Summary rows (optional) -----
  if (summaryRows.length > 0) {
    doc.setFontSize(11);
    doc.setFont(undefined, 'bold');
    doc.text('Summary', 14, currentY);
    currentY += 6;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');

    summaryRows.forEach((row) => {
      doc.text(`${row.label}:`, 14, currentY);
      doc.setFont(undefined, 'bold');
      doc.text(String(row.value), 70, currentY);
      doc.setFont(undefined, 'normal');
      currentY += 6;
    });

    currentY += 4;
  }

  // ----- Main table -----
  const tableHeaders = columns.map((c) => c.label);
  const tableBody = rows.map((row) =>
    columns.map((col) => {
      const val = row[col.key];
      if (col.formatter) return col.formatter(val, row);
      if (val === null || val === undefined) return '';
      return String(val);
    })
  );

  doc.autoTable({
    head: [tableHeaders],
    body: tableBody,
    startY: currentY,
    styles: { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: [26, 115, 232], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [249, 250, 251] },
    margin: { left: 14, right: 14 },
  });

  // ----- Footer -----
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(150, 150, 150);
    doc.text(
      `Page ${i} of ${pageCount} — LRA Inventory System`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 8,
      { align: 'center' }
    );
  }

  doc.save(filename);
}

// ---------- Formatters ----------
export function fmtCurrency(value) {
  if (value === null || value === undefined) return '$0.00';
  return `$${parseFloat(value).toFixed(2)}`;
}

export function fmtNumber(value) {
  if (value === null || value === undefined) return '0';
  return String(parseInt(value) || 0);
}

export function fmtDate(value) {
  if (!value) return '—';
  return new Date(value).toLocaleDateString();
}
